import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { Toaster } from './components/ui/sonner';
import { trackPageView } from './lib/api';
import { warmBackend, keepEnginesRunning } from './lib/warm';
import './App.css';

import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import ChromeWave from './components/layout/ChromeWave';

import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import EcosystemPage from './pages/EcosystemPage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import ContactPage from './pages/ContactPage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function PageTracker() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (!pathname.startsWith('/admin')) {
      trackPageView({ page: pathname, referrer: document.referrer }).catch(() => {});
    }
  }, [pathname]);
  return null;
}

function AnimatedRoutes() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');

  return (
    <>
      <ScrollToTop />
      <PageTracker />
      {!isAdmin && <Header />}
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/ecosystem" element={<EcosystemPage />} />
          <Route path="/powered/:slug" element={<ProjectDetailPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/journal" element={<BlogPage />} />
          <Route path="/journal/:slug" element={<BlogPostPage />} />
          <Route path="/admin" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
        </Routes>
      </AnimatePresence>
      {!isAdmin && <Footer />}
      {!isAdmin && <div className="crt-overlay" aria-hidden="true" />}
    </>
  );
}

export default function App() {
  // Wake the free-tier API on first load and keep it up while the tab is open.
  useEffect(() => { warmBackend(); return keepEnginesRunning(); }, []);

  React.useEffect(() => {
    const removeWatermarks = () => {
      const badge = document.getElementById('emergent-badge');
      if (badge) badge.remove();
      document.querySelectorAll('a[href*="emergent.sh"]').forEach(el => el.remove());
    };
    removeWatermarks();
    const observer = new MutationObserver(removeWatermarks);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  return (
    <Router>
      <div className="min-h-screen bg-[#070A0E] text-white relative">
        <ChromeWave />
        <AnimatedRoutes />
        <Toaster position="bottom-right" theme="dark" />
      </div>
    </Router>
  );
}
